import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { applyMeta, pageMeta } from '../seo.js';
import { EDITORIAL_ROUTES } from '../content/index.js';
import { openCookieSettings } from '../components/CookieConsent.jsx';
import { CONTACT_EMAIL } from './Contact.jsx';

const META = EDITORIAL_ROUTES.find(r => r.path === '/privacy');

const LAST_UPDATED = '28 July 2026';

export default function Privacy() {
  useEffect(() => applyMeta(pageMeta({
    path: '/privacy',
    title: META.title,
    description: META.description,
  })), []);

  return (
    <article className="page-prose">
      <h1 className="page-heading">Privacy policy</h1>
      <p className="page-subheading">Last updated {LAST_UPDATED}.</p>

      <h2>Who we are</h2>
      <p>
        TalkMuch is an independent site published at talkmuch.org that measures how much
        podcast hosts talk. For data-protection purposes the publisher of this site is the
        data controller. You can reach us at{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>

      <h2>What we collect</h2>
      <p>
        You can browse TalkMuch without giving us anything. There is no account system, no
        newsletter, and no login. We do not ask for your name, address or payment details,
        because there is nothing here to buy.
      </p>
      <p>The data that does exist falls into three groups.</p>
      <ul>
        <li>
          <strong>Things your browser stores locally.</strong> Your light or dark theme
          choice, your cookie preferences, and any episode ratings you set are kept in your
          own browser using local storage. They never leave your device and we cannot read
          them.
        </li>
        <li>
          <strong>Your consent choice.</strong> When you accept or decline non-essential
          cookies we keep a record of that decision, so we can prove consent was given and
          honour it on later visits. See the consent record section below.
        </li>
        <li>
          <strong>Advertising and measurement data.</strong> Collected by third parties, and
          only if you agree to it. See the next section.
        </li>
      </ul>

      <h2>Advertising, and Google</h2>
      <p>
        TalkMuch carries advertising, and <strong>Google is our advertising partner</strong>.
        Google, as a third-party vendor, uses cookies to serve ads on this site.
      </p>
      <ul>
        <li>
          Google's use of advertising cookies enables it and its partners to serve ads to you
          based on your visit to this site and other sites on the internet.
        </li>
        <li>
          Third-party vendors and ad networks, including Google, may use cookies, device
          identifiers and similar technologies to collect information such as your IP
          address, approximate location, browser and device type, the pages you view on this
          site, and how you interact with ads.
        </li>
        <li>
          You can opt out of personalised advertising by visiting{' '}
          <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">
            Google Ads Settings
          </a>
          , or opt out of a third-party vendor's use of cookies for personalised advertising
          at{' '}
          <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">
            aboutads.info/choices
          </a>
          .
        </li>
        <li>
          Google describes how it uses data from sites that use its services at{' '}
          <a
            href="https://policies.google.com/technologies/partner-sites"
            target="_blank"
            rel="noopener noreferrer"
          >
            policies.google.com/technologies/partner-sites
          </a>
          .
        </li>
      </ul>
      <p>
        Advertising appears on this site whatever you choose. What your choice controls is
        whether it is personalised. Agree, and Google may use cookies and the profile it
        holds to select ads it expects you to respond to. Decline, or make no choice at all,
        and advertising is served non-personalised: it is chosen from the page you are
        reading rather than from anything about you. Google still sets a small number of
        cookies in that case, to limit how often the same ad repeats and to detect fraud,
        but it does not use your browsing history to pick what you see.
      </p>

      <h2>Cookies we use</h2>
      <ul>
        <li>
          <strong>Necessary.</strong> Required for the site to function and to remember that
          you have expressed a cookie preference at all. These cannot be switched off.
        </li>
        <li>
          <strong>Analytics.</strong> Optional. Used only to understand which episodes and
          pages people find useful, in aggregate.
        </li>
        <li>
          <strong>Advertising.</strong> Set by Google and its partners as described above.
          Whether they are used to personalise the ads you see is optional; a small number
          are used for frequency capping and fraud prevention either way.
        </li>
      </ul>
      <p>
        You can change your mind at any time.{' '}
        <button type="button" className="link-button" onClick={openCookieSettings}>
          Reopen cookie preferences
        </button>
        . Withdrawing consent is as easy as giving it, and takes effect immediately.
      </p>

      <h2>The consent record</h2>
      <p>
        When you make a cookie choice we keep a record of it so that we can demonstrate,
        if asked, that consent was freely given and what it covered. A consent record holds
        the choice itself, the time it was made, the version of the notice you were shown,
        and a truncated, non-identifying form of your IP address used to establish the
        approximate region the consent came from. It does not contain your name, your email
        address, or your browsing history.
      </p>
      <p>
        Consent records are held for as long as we are required to be able to evidence them
        and are then deleted. When you withdraw consent, the corresponding records are
        deleted rather than merely flagged, and the withdrawal is passed on to the
        advertising and analytics partners it applied to.
      </p>

      <h2>Podcast content on this site</h2>
      <p>
        The talk-time figures, question counts and other statistics on TalkMuch are our own
        measurements, produced by the process described in{' '}
        <Link to="/methodology">the methodology</Link>. Episode titles, artwork and short
        publisher summaries are taken from public podcast feeds and remain the property of
        their publishers. We do not publish transcripts. Publishers wanting a change or a
        removal should write to <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>

      <h2>Your rights</h2>
      <p>
        If you are in the UK or the European Economic Area you have the right to access the
        personal data we hold about you, to have it corrected or erased, to restrict or
        object to how it is processed, to receive a copy in a portable form, and to withdraw
        consent at any time without affecting processing that already happened. You also
        have the right to complain to your national data protection authority.
      </p>
      <p>
        To exercise any of these, email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Because we hold so little
        about any individual, we may need you to identify the specific consent record you are
        asking about.
      </p>

      <h2>Children</h2>
      <p>
        TalkMuch is not directed at children under 13 and we do not knowingly collect data
        from them.
      </p>

      <h2>Changes</h2>
      <p>
        If this policy changes materially we will update the date at the top of the page and,
        where the change affects what you have consented to, ask you again.
      </p>
    </article>
  );
}
